import React from "react";

/**
 * HeritageSeal — sage circular seal with stylised dahlia + EST. 1932.
 * Sizes: 60 | 90 | 160 | custom
 */
export function HeritageSeal({ size = 160, className = "", label = true }) {
  const px = typeof size === "number" ? `${size}px` : size;
  return (
    <div
      data-testid="heritage-seal"
      className={`inline-flex items-center justify-center ${className}`}
      style={{ width: px, height: px }}
    >
      <svg viewBox="0 0 200 200" width="100%" height="100%" aria-label="Heritage Dahlias est. 1932">
        <defs>
          <path id="hd-arc-top" d="M 30 100 A 70 70 0 0 1 170 100" />
          <path id="hd-arc-bot" d="M 30 100 A 70 70 0 0 0 170 100" />
        </defs>

        {/* Scalloped outer ring */}
        <circle cx="100" cy="100" r="92" fill="none" stroke="#7E8C5B" strokeWidth="1.2" strokeDasharray="2 4" />

        {/* Solid sage seal */}
        <circle cx="100" cy="100" r="84" fill="#7E8C5B" />
        <circle cx="100" cy="100" r="84" fill="none" stroke="#5F6B43" strokeWidth="1" />
        <circle cx="100" cy="100" r="78" fill="none" stroke="#FBF7EC" strokeWidth="1" strokeDasharray="1 3" />

        {/* HERITAGE arched */}
        {label && (
          <text fill="#FBF7EC" fontFamily="Cormorant Garamond, Georgia, serif" fontWeight="700" letterSpacing="6" fontSize="16">
            <textPath href="#hd-arc-top" startOffset="50%" textAnchor="middle">HERITAGE</textPath>
          </text>
        )}

        {/* Dahlia silhouette */}
        <g transform="translate(100 108)" fill="none" stroke="#FBF7EC" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          {/* outer petals */}
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i * 360) / 12;
            return (
              <g key={`o-${i}`} transform={`rotate(${a})`}>
                <path d="M0 -32 C 5 -22, 5 -14, 0 -8 C -5 -14, -5 -22, 0 -32 Z" fill="#FBF7EC" />
              </g>
            );
          })}
          {/* mid petals */}
          {Array.from({ length: 10 }).map((_, i) => {
            const a = (i * 360) / 10 + 18;
            return (
              <g key={`m-${i}`} transform={`rotate(${a})`}>
                <path d="M0 -22 C 4 -15, 4 -10, 0 -6 C -4 -10, -4 -15, 0 -22 Z" fill="#FBF7EC" />
              </g>
            );
          })}
          {/* inner petals */}
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * 360) / 8 + 22;
            return (
              <g key={`i-${i}`} transform={`rotate(${a})`}>
                <path d="M0 -14 C 3 -10, 3 -6, 0 -4 C -3 -6, -3 -10, 0 -14 Z" fill="#FBF7EC" />
              </g>
            );
          })}
          {/* center */}
          <circle r="3" fill="#7E8C5B" stroke="#FBF7EC" strokeWidth="1.2" />
          {/* leaves */}
          <path d="M -14 18 C -22 22, -28 22, -32 18 C -28 16, -22 14, -14 14 Z" fill="#FBF7EC" />
          <path d="M 14 18 C 22 22, 28 22, 32 18 C 28 16, 22 14, 14 14 Z" fill="#FBF7EC" />
          <path d="M -2 14 L -2 28 L 2 28 L 2 14 Z" fill="#FBF7EC" />
        </g>

        {/* EST. — 1932 marks */}
        {label && (
          <>
            <text x="42" y="118" fill="#FBF7EC" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="10" letterSpacing="2">EST.</text>
            <line x1="38" y1="123" x2="64" y2="123" stroke="#FBF7EC" strokeWidth="0.8" />
            <text x="138" y="118" fill="#FBF7EC" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="10" letterSpacing="2">1932</text>
            <line x1="134" y1="123" x2="162" y2="123" stroke="#FBF7EC" strokeWidth="0.8" />
          </>
        )}
      </svg>
    </div>
  );
}

/** Italic letter-spaced kicker label */
export function Kicker({ children, className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-2 kicker ${className}`} data-testid="kicker">
      <span aria-hidden>·</span>
      <span>{children}</span>
      <span aria-hidden>·</span>
    </div>
  );
}

/** Hairline divider with italic centred label */
export function SectionDivider({ label, className = "" }) {
  return (
    <div className={`divider ${className}`} data-testid="section-divider">
      <span>· {label} ·</span>
    </div>
  );
}

/** Page header: kicker + serif title + italic subtitle + divider */
export function PageHeader({ kicker, title, subtitle, testId = "page-header" }) {
  return (
    <header className="text-center pt-8 pb-6 animate-fade-up" data-testid={testId}>
      {kicker && <Kicker>{kicker}</Kicker>}
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-sage-dark mt-3 tracking-wide">
        {title}
      </h1>
      {subtitle && (
        <p className="font-display italic text-ink-soft text-lg mt-2">
          <span className="inline-flex items-center gap-3">
            <span className="block h-px w-8 bg-heritage-border" />
            {subtitle}
            <span className="block h-px w-8 bg-heritage-border" />
          </span>
        </p>
      )}
    </header>
  );
}
